import { getChatCompletion } from './aiIntegrations/chatCompletion';

const PROVIDER = 'OPEN_AI';
const DEFAULT_MODEL = 'gpt-4o';
const CHAT_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;

/**
 * Check if the AI service is available (Lambda endpoint configured)
 */
export function isOpenAIAvailable() {
  return !!CHAT_ENDPOINT;
}

/**
 * Check if quota is exceeded - Lambda handles this server-side
 */
export function isQuotaExceeded() {
  return false;
}

/**
 * Generates a GPT response for funnel building assistance.
 */
export async function getFunnelBuildingAssistance(userMessage, reasoningEffort = 'medium', verbosity = 'medium') {
  const response = await getChatCompletion(
    PROVIDER,
    DEFAULT_MODEL,
    [
      {
        role: 'system',
        content: 'You are an expert marketing funnel strategist and AI assistant specializing in creating high-converting marketing funnels for online courses, coaching programs, podcasts, newsletters, digital downloads, and landing pages. Provide actionable, specific advice.'
      },
      { role: 'user', content: userMessage }
    ],
    { max_completion_tokens: 2000, reasoning_effort: reasoningEffort }
  );
  return response?.choices?.[0]?.message?.content;
}

/**
 * Generates funnel strategy based on business type and goals.
 */
export async function generateFunnelStrategy(businessType, targetAudience, goals) {
  const prompt = `Create a comprehensive marketing funnel strategy for a ${businessType} business targeting ${targetAudience} with goals: ${goals}. Include lead magnets, funnel stages, automation sequences, and conversion optimization tips.`;

  const response = await getChatCompletion(
    PROVIDER,
    DEFAULT_MODEL,
    [
      {
        role: 'system',
        content: 'You are an expert marketing funnel strategist. Provide detailed, actionable funnel strategies with specific tactics, tools, and optimization recommendations. Always respond with valid JSON.'
      },
      { role: 'user', content: prompt + '\n\nRespond with JSON containing: strategy_overview (string), lead_magnets (array of {title, description, format}), funnel_stages (array of {stage, goal, tactics[], metrics[]}), automation_sequences (array of {name, trigger, sequence[]}), optimization_tips (array of strings), recommended_tools (array of strings).' }
    ],
    { max_completion_tokens: 3000 }
  );

  const content = response?.choices?.[0]?.message?.content || '{}';
  try {
    return JSON.parse(content);
  } catch {
    return {
      strategy_overview: content,
      lead_magnets: [],
      funnel_stages: [],
      automation_sequences: [],
      optimization_tips: [],
      recommended_tools: []
    };
  }
}

/**
 * Generates content ideas for marketing funnels
 */
export async function generateContentIdeas(contentType, funnelStage, audience) {
  const prompt = `Generate 10 ${contentType} content ideas for the ${funnelStage} stage of a marketing funnel targeting ${audience}. Make them engaging, actionable, and conversion-focused. Respond with JSON: { content_ideas: [{title, description, call_to_action, estimated_performance}] }`;

  const response = await getChatCompletion(
    PROVIDER,
    DEFAULT_MODEL,
    [
      {
        role: 'system',
        content: 'You are a content marketing expert. Generate creative, engaging content ideas that drive conversions. Always respond with valid JSON.'
      },
      { role: 'user', content: prompt }
    ],
    { max_completion_tokens: 2000 }
  );

  const content = response?.choices?.[0]?.message?.content || '{}';
  try {
    return JSON.parse(content);
  } catch {
    return { content_ideas: [] };
  }
}

/**
 * Generate AI insights for leads
 */
export async function generateLeadInsights(searchData, historicalData) {
  try {
    const response = await getChatCompletion(
      PROVIDER,
      DEFAULT_MODEL,
      [
        {
          role: 'system',
          content: 'You are an expert lead generation analyst. Analyze lead data and provide actionable insights. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Analyze this lead search data and provide insights:\n\nSearch Data: ${JSON.stringify(searchData)}\nHistorical Data: ${JSON.stringify(historicalData)}\n\nRespond with JSON: { insights: string[], recommendations: string[], quality_score: number, top_industries: string[], best_channels: string[] }`
        }
      ],
      { max_completion_tokens: 1500 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return { insights: [], recommendations: [], quality_score: 75, top_industries: [], best_channels: [] };
    }
  } catch (error) {
    console.error('Error generating lead insights:', error);
    return { insights: [], recommendations: [], quality_score: 0, top_industries: [], best_channels: [] };
  }
}

/**
 * Generate sales closing strategy
 */
export async function generateSalesClosingStrategy(leadData, salesContext) {
  try {
    const response = await getChatCompletion(
      PROVIDER,
      DEFAULT_MODEL,
      [
        {
          role: 'system',
          content: 'You are an expert sales strategist specializing in closing deals. Provide actionable closing strategies. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Generate a sales closing strategy for this lead:\n\nLead Data: ${JSON.stringify(leadData)}\nSales Context: ${JSON.stringify(salesContext)}\n\nRespond with JSON: { strategy: string, key_points: string[], objection_handlers: {objection: string, response: string}[], closing_techniques: string[], follow_up_plan: string }`
        }
      ],
      { max_completion_tokens: 2000 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return { strategy: content, key_points: [], objection_handlers: [], closing_techniques: [], follow_up_plan: '' };
    }
  } catch (error) {
    console.error('Error generating sales closing strategy:', error);
    return null;
  }
}

/**
 * Generate closing script
 */
export async function generateClosingScript(leadData, strategy, scriptType) {
  try {
    const response = await getChatCompletion(
      PROVIDER,
      DEFAULT_MODEL,
      [
        {
          role: 'system',
          content: 'You are an expert sales script writer. Create compelling, natural-sounding sales scripts. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Create a ${scriptType || 'closing'} script for:\n\nLead: ${JSON.stringify(leadData)}\nStrategy: ${JSON.stringify(strategy)}\n\nRespond with JSON: { script: string, key_phrases: string[], tone: string, estimated_duration: string }`
        }
      ],
      { max_completion_tokens: 1500 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return { script: content, key_phrases: [], tone: 'professional', estimated_duration: '5-10 minutes' };
    }
  } catch (error) {
    console.error('Error generating closing script:', error);
    return null;
  }
}

/**
 * Analyze closing opportunity
 */
export async function analyzeClosingOpportunity(leadData, interactionHistory) {
  try {
    const response = await getChatCompletion(
      PROVIDER,
      DEFAULT_MODEL,
      [
        {
          role: 'system',
          content: 'You are an expert sales analyst. Analyze sales opportunities and provide closing probability assessments. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Analyze this closing opportunity:\n\nLead: ${JSON.stringify(leadData)}\nHistory: ${JSON.stringify(interactionHistory)}\n\nRespond with JSON: { closing_probability: number, readiness_score: number, key_signals: string[], recommended_action: string, timing: string, risk_factors: string[] }`
        }
      ],
      { max_completion_tokens: 1000 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return { closing_probability: 0.5, readiness_score: 50, key_signals: [], recommended_action: '', timing: '', risk_factors: [] };
    }
  } catch (error) {
    console.error('Error analyzing closing opportunity:', error);
    return null;
  }
}

/**
 * Analyze communication performance
 */
export async function analyzeCommunicationPerformance(communicationData, metrics) {
  try {
    const response = await getChatCompletion(
      PROVIDER,
      DEFAULT_MODEL,
      [
        {
          role: 'system',
          content: 'You are a communication analytics expert. Analyze communication performance and provide optimization recommendations. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Analyze communication performance:\n\nData: ${JSON.stringify(communicationData)}\nMetrics: ${JSON.stringify(metrics)}\n\nRespond with JSON: { overall_score: number, strengths: string[], weaknesses: string[], recommendations: string[], best_performing_channel: string, optimal_send_times: string[] }`
        }
      ],
      { max_completion_tokens: 1500 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return { overall_score: 0, strengths: [], weaknesses: [], recommendations: [], best_performing_channel: '', optimal_send_times: [] };
    }
  } catch (error) {
    console.error('Error analyzing communication performance:', error);
    return null;
  }
}

/**
 * Generate outreach content
 */
export async function generateOutreachContent(leadContext, contentType, contextPrompt) {
  try {
    const response = await getChatCompletion(
      PROVIDER,
      DEFAULT_MODEL,
      [
        {
          role: 'system',
          content: 'You are an expert copywriter specializing in B2B outreach and sales communications. Create compelling, personalized content that drives engagement and conversions. Respond with valid JSON.'
        },
        {
          role: 'user',
          content: `Generate ${contentType} outreach content:\n\nLead Context: ${JSON.stringify(leadContext)}\nInstructions: ${contextPrompt}\n\nRespond with JSON: { subject: string, body: string, call_to_action: string, personalization_tokens: string[], estimated_response_rate: string }`
        }
      ],
      { max_completion_tokens: 1500 }
    );

    const content = response?.choices?.[0]?.message?.content || '{}';
    try {
      return JSON.parse(content);
    } catch {
      return { subject: '', body: content, call_to_action: '', personalization_tokens: [], estimated_response_rate: 'N/A' };
    }
  } catch (error) {
    console.error('Error generating outreach content:', error);
    return { subject: '', body: '', call_to_action: '', personalization_tokens: [], estimated_response_rate: 'N/A' };
  }
}

export default {
  isOpenAIAvailable,
  isQuotaExceeded,
  getFunnelBuildingAssistance,
  generateFunnelStrategy,
  generateContentIdeas,
  generateLeadInsights,
  generateSalesClosingStrategy,
  generateClosingScript,
  analyzeClosingOpportunity,
  analyzeCommunicationPerformance,
  generateOutreachContent
};
